
import { useState, useEffect } from "react";
import { AppDescription } from "./AppDescription";
import { AppCategoryTagsCompact } from "./AppCategoryTagsCompact";
import { useAppStoreLookup } from "@/hooks/useAppStoreLookup";
import { App } from "@/types/app";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";

interface AppInfoTabProps {
  app: App;
}

interface AppStoreApp {
  trackId: number;
  bundleId: string;
  appName: string;
  iconUrl?: string;
  description?: string;
  category: string;
  version?: string;
  developerName: string;
}

interface LookupResult {
  bundleId: string;
  app?: AppStoreApp;
  found: boolean;
  error?: string;
}

export const AppInfoTab = ({ app }: AppInfoTabProps) => {
  const [localApp, setLocalApp] = useState(app);
  const [appStoreData, setAppStoreData] = useState<AppStoreApp | null>(null);
  const [isLoadingAppStore, setIsLoadingAppStore] = useState(false);
  const { lookupByBundleId } = useAppStoreLookup();
  const updateSystemStatus = useMutation(api.apps.update.system);
  const { toast } = useToast();
  
  // Fetch App Store data when app changes (non-blocking background operation)
  useEffect(() => {
    const fetchAppStoreData = async () => {
      if (!app.bundle_id) return;
      
      setIsLoadingAppStore(true);
      
      try {
        // Use a timeout to prevent hanging on slow/failed requests
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        );
        
        const result = await Promise.race([
          lookupByBundleId(app.bundle_id) as Promise<LookupResult>,
          timeoutPromise
        ]);
        
        if (result && result.found && result.app) {
          setAppStoreData(result.app);
          // Only update description and version, NOT category
          setLocalApp(prev => ({
            ...prev,
            description: result.app?.description || prev.description,
            version_code: result.app?.version || prev.version_code,
            app_icon_url: result.app?.iconUrl || prev.app_icon_url
            // Keep the existing category - don't override it
          }));
        }
      } catch (error) {
        // Silently fail for CORS/network errors to not block UI
        console.debug('App Store lookup skipped (network/CORS issue):', app.bundle_id);
      } finally {
        setIsLoadingAppStore(false);
      }
    };

    // Delay the App Store lookup to not block initial render
    const timeoutId = setTimeout(fetchAppStoreData, 100);
    
    return () => clearTimeout(timeoutId);
  }, [app.bundle_id, lookupByBundleId]);

  // Update local app state when app prop changes
  useEffect(() => {
    console.log('AppInfoTab: App prop changed, updating local state', app);
    setLocalApp(app);
  }, [app]);

  const handleTagsUpdate = (newTagIds: string[]) => {
    console.log('AppInfoTab: Updating tags to:', newTagIds);
    setLocalApp(prev => ({
      ...prev,
      tag_ids: newTagIds
    }));
  };

  const handleCategoryUpdate = (newCategory: string) => {
    console.log('AppInfoTab: Updating category to:', newCategory);
    setLocalApp(prev => ({
      ...prev,
      category: newCategory,
      // Clear tags when category changes
      tag_ids: []
    }));
  };

  const handleSystemStatusUpdate = async (isSystem: boolean) => {
    try {
      await updateSystemStatus({
        app: app.id as any,
        system: isSystem
      });
      setLocalApp(prev => ({
        ...prev,
        system_app: isSystem
      }));
      toast({
        title: "Success",
        description: `App marked as ${isSystem ? "System" : "App Store"} app`,
      });
    } catch (error) {
      console.error('Failed to update system status:', error);
      toast({
        title: "Error",
        description: "Failed to update app type",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-3 space-y-4">
        {/* App Store Info - only show when data is available */}
        {appStoreData && (
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-green-800 mb-2">App Store Information</h4>
            <div className="space-y-2 text-sm text-green-700">
              <div><span className="font-medium">Developer:</span> {appStoreData.developerName}</div>
              <div><span className="font-medium">Store Version:</span> {appStoreData.version}</div>
              <div><span className="font-medium">Store Category:</span> {appStoreData.category}</div>
            </div>
          </div>
        )}

        {/* App Type Dropdown */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              App Type
            </Label>
            <Select 
              value={localApp.system_app ? "system" : "app-store"}
              onValueChange={(value) => handleSystemStatusUpdate(value === "system")}
            >
              <SelectTrigger className="bg-white border border-gray-300 shadow-sm z-50">
                <SelectValue placeholder="Select app type" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                <SelectItem value="app-store" className="hover:bg-gray-100">App Store</SelectItem>
                <SelectItem value="system" className="hover:bg-gray-100">System App</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {localApp.system_app ? "This is a system app" : "This is an App Store app"}
            </p>
          </div>
        </div>
        
        <AppCategoryTagsCompact 
          app={localApp} 
          onTagsUpdate={handleTagsUpdate}
          onCategoryUpdate={handleCategoryUpdate}
        />
        <AppDescription app={localApp} />
      </div>
    </div>
  );
};
